import type MarkdownIt from 'markdown-it';
import type StateCore from 'markdown-it/lib/rules_core/state_core.mjs';

/**
 * This plugin handles blockquotes nested inside list items.
 * Since ADF doesn't support blockquotes as children of listItems,
 * we transform blockquote content into emphasized (italic) paragraphs.
 *
 * Before: 1. Item\n   > "Quote"
 * After:  1. Item\n   _"Quote"_
 *
 * This allows quotes within lists to render gracefully as italicized text
 * rather than failing due to ADF schema constraints.
 */
const listBlockquoteFallback = (state: StateCore) => {
	const tokens = state.tokens;
	let listDepth = 0;

	for (let i = 0; i < tokens.length; i++) {
		const token = tokens[i];

		// Track list nesting depth
		if (token.type === 'ordered_list_open' || token.type === 'bullet_list_open') {
			listDepth++;
		} else if (token.type === 'ordered_list_close' || token.type === 'bullet_list_close') {
			listDepth--;
		}

		// If we're inside a list and encounter a blockquote, transform it
		if (listDepth > 0 && token.type === 'blockquote_open') {
			// Find the matching blockquote_close
			let closeIndex = -1;
			let depth = 1;

			for (let j = i + 1; j < tokens.length; j++) {
				if (tokens[j].type === 'blockquote_open') {
					depth++;
				} else if (tokens[j].type === 'blockquote_close') {
					depth--;
					if (depth === 0) {
						closeIndex = j;
						break;
					}
				}
			}

			if (closeIndex !== -1) {
				// Mark the blockquote tokens for removal
				token.type = 'blockquote_fallback_open';
				token.hidden = true;
				tokens[closeIndex].type = 'blockquote_fallback_close';
				tokens[closeIndex].hidden = true;

				// Process the content inside the blockquote
				// We need to wrap the inline content with emphasis marks
				for (let j = i + 1; j < closeIndex; j++) {
					const innerToken = tokens[j];

					if (innerToken.type === 'inline' && innerToken.children) {
						// Wrap all children with em marks
						const emOpen = new state.Token('em_open', 'em', 1);
						emOpen.block = false;

						const emClose = new state.Token('em_close', 'em', -1);
						emClose.block = false;

						innerToken.children = [emOpen, ...innerToken.children, emClose];
					}
				}
			}
		}
	}

	// Filter out the hidden blockquote fallback tokens
	state.tokens = tokens.filter(
		(t) => t.type !== 'blockquote_fallback_open' && t.type !== 'blockquote_fallback_close',
	);
};

const mdPluginListBlockquoteFallback = (md: MarkdownIt): void => {
	md.core.ruler.push('list_blockquote_fallback', listBlockquoteFallback);
};

export default mdPluginListBlockquoteFallback;
